import AuthPiece from "aws-amplify-react/lib/Auth/AuthPiece";
import AmplifyAuthenticator from "aws-amplify-react/lib/Auth/Authenticator";
import { useEffect, useState } from "react";

import aws_exports from "config/aws/aws-exports";
import { containerSettings } from "config/components/toast";
import { StoreProvider } from "context";
import { PendoInitializer } from "base/components";
import { Layout } from "@/layout";
import { Toast } from "@/ui";
import { ApolloProviderWrapper } from "apollo/components";
import { applyPostAuthState } from "auth/services";
import {
    GoogleMapsLoadProvider,
    useGoogleMapsLoad,
} from "auth/hooks/useGoogleMapsLoad";
import { AppBootScreen } from "./AppBootScreen";
import {
    AuthLoading,
    ConfirmSignIn,
    ConfirmSignUp,
    ForgotPassword,
    RequireNewPassword,
    SignIn,
    TOTPSetup,
    VerifyContact,
} from "./auth-ui";

/* Amplify AuthPiece / Authenticator subclasses have no usable React types. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AuthenticatorBase: any = AmplifyAuthenticator;

/** Avoid logo flash when Maps resolves from cache in a few ms. */
const BOOT_SCREEN_DELAY_MS = 250;

function useDelayedBoot(pending: boolean) {
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (!pending) {
            setShow(false);
            return;
        }
        const id = window.setTimeout(() => setShow(true), BOOT_SCREEN_DELAY_MS);
        return () => window.clearTimeout(id);
    }, [pending]);

    return pending && show;
}

class Authenticator extends AuthenticatorBase {
    checkContact(
        user: unknown,
        changeState: (state: string, data?: unknown) => void,
    ) {
        applyPostAuthState(user, (state, data) => {
            if (
                this.state?.authState === "signedIn" &&
                state === "verifyContact"
            ) {
                return;
            }
            changeState(state, data);
        });
    }
}

/**
 * Layout gate — wait for Maps (or load error).
 * Boot screen only after a short delay so fast/cached loads skip the flash.
 */
function SignedInShell() {
    const { isLoaded, loadError } = useGoogleMapsLoad();
    const waitingForMaps = !isLoaded && !loadError;
    const showBootScreen = useDelayedBoot(waitingForMaps);

    if (waitingForMaps) {
        return showBootScreen ? <AppBootScreen /> : null;
    }

    return (
        <ApolloProviderWrapper>
            <StoreProvider>
                <PendoInitializer />
                <Layout />
            </StoreProvider>
            <Toast {...containerSettings} stacked />
        </ApolloProviderWrapper>
    );
}

class AuthenticatorWrapper extends AuthPiece {
    _validAuthStates = [];

    constructor(props) {
        super(props);
        this._validAuthStates = ["signedIn"];
    }

    showComponent = () => <SignedInShell />;
}

/** Amplify `container={false}` → EmptyContainer (no Amplify page chrome). */
export const AWSAuthenticatorWrapper = () => {
    /* Amplify AuthPiece subclasses have no usable React types. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const asPiece = (C: unknown) => C as any;

    const AuthWrapper = asPiece(AuthenticatorWrapper);
    const SignInPiece = asPiece(SignIn);
    const ConfirmSignInPiece = asPiece(ConfirmSignIn);
    const ConfirmSignUpPiece = asPiece(ConfirmSignUp);
    const RequireNewPasswordPiece = asPiece(RequireNewPassword);
    const VerifyContactPiece = asPiece(VerifyContact);
    const ForgotPasswordPiece = asPiece(ForgotPassword);
    const TOTPSetupPiece = asPiece(TOTPSetup);
    const AuthLoadingPiece = asPiece(AuthLoading);
    const AuthenticatorPiece = asPiece(Authenticator);

    return (
        <GoogleMapsLoadProvider>
            <AuthenticatorPiece
                amplifyConfig={aws_exports}
                container={false}
                hideDefault
            >
                <SignInPiece />
                <ConfirmSignInPiece />
                <ConfirmSignUpPiece />
                <RequireNewPasswordPiece />
                <VerifyContactPiece />
                <ForgotPasswordPiece />
                <TOTPSetupPiece />
                <AuthLoadingPiece />
                <AuthWrapper />
            </AuthenticatorPiece>
        </GoogleMapsLoadProvider>
    );
};
